import React, { useEffect, useState } from 'react';
import { Box, Typography, Avatar, Skeleton } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import { fetchInvitePreview, type InvitePreview } from '../../services/communityService';
import { CommunityInviteJoinModal } from './CommunityInviteJoinModal';

interface Props {
  token: string;
  href: string;
  onClick?: (e: React.MouseEvent) => void;
}

function inviteStatusHint(preview: InvitePreview | null): string {
  if (!preview) return 'Tap to open this invitation';
  if (preview.invite_status === 'already_member') {
    return "You're already in this community";
  }
  if (preview.invite_status === 'valid') {
    return 'Tap to join this community';
  }
  if (preview.invite_status === 'expired' || preview.invite_status === 'exhausted') {
    return 'This invite link is no longer active';
  }
  return 'This invite link is not valid';
}

export const CommunityInviteCard: React.FC<Props> = ({ token, href: _href, onClick }) => {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchInvitePreview(token)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const title = preview?.community_name || 'Community invitation';
  const subtitle = inviteStatusHint(preview);

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.(e);
    setModalOpen(true);
  };

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen(e as unknown as React.MouseEvent);
          }
        }}
        sx={{
          display: 'block',
          mt: 0.75,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.08)',
          bgcolor: 'rgba(255,255,255,0.85)',
          color: 'inherit',
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,1)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.25,
            py: 1,
            borderLeft: '3px solid #0f9d58',
          }}
        >
          {loading ? (
            <Skeleton variant="circular" width={44} height={44} />
          ) : (
            <Avatar
              src={preview?.community_avatar_url || undefined}
              sx={{ width: 44, height: 44, bgcolor: '#0f9d58', flexShrink: 0 }}
            >
              <GroupsIcon sx={{ fontSize: 22 }} />
            </Avatar>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.68rem',
                color: '#8696a0',
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
              }}
            >
              Community invite
            </Typography>
            {loading ? (
              <>
                <Skeleton width="70%" height={18} sx={{ mt: 0.5 }} />
                <Skeleton width="90%" height={14} sx={{ mt: 0.5 }} />
              </>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: '0.88rem',
                    fontWeight: 600,
                    color: '#111b21',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mt: 0.15,
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#667781',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    mt: 0.25,
                  }}
                >
                  {subtitle}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            px: 1.25,
            py: 0.75,
            borderTop: '1px solid rgba(0,0,0,0.06)',
            bgcolor: 'rgba(15, 157, 88, 0.06)',
          }}
        >
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#0f9d58' }}>
            {preview?.invite_status === 'already_member' ? 'Open community' : 'Join community'}
          </Typography>
        </Box>
      </Box>

      <CommunityInviteJoinModal
        open={modalOpen}
        token={token}
        preview={preview}
        previewLoading={loading}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};
